import { isNumber } from '@beeline.com/bp-design-system';
import { formatIntlMessage, IntlModuleTypes } from '@enterprise/common';
import {
  ExportSource,
  NotificationStatusModel,
  OcReportSourceTypeModel
} from 'common/header/notifications/Notifications.types';
import { MessageValue } from 'locale';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { NotificationTypeModel } from 'src/graphql/types';
import messages from './exportNotification.intl.json';

export type ExportNotificationIntlKey = IntlModuleTypes.IntlKeys<typeof messages>;

export const useFormatExportNotificationMessage = () => {
  const intlShape = useIntl();
  return (message: ExportNotificationIntlKey, variables?: Record<string, MessageValue>) =>
    formatIntlMessage(intlShape, messages, message, variables);
};

interface MessagesByStatusProps {
  status: NotificationStatusModel;
  actualCount: number;
  totalCount: number;
  prefix: string;
}

interface MessagesByFeatureProps {
  featureKey: 'EXPORT_TIMESHEET_INCLUDE_EMPTY_ALLOCATION' | string;
  enabled: boolean;
  count: string;
}

interface SourceNameProps {
  sourceType: OcReportSourceTypeModel | string | undefined | null;
  name: string;
}

interface EntityNameProps {
  entityType: ExportSource | undefined | null;
}

interface TypeNameProps {
  type: NotificationTypeModel | undefined | null;
}

export function useExportNotificationsIntlModule() {
  const format = useFormatExportNotificationMessage();
  return useMemo(() => {
    return {
      getMessagesByStatus: ({ status, totalCount, actualCount, prefix }: MessagesByStatusProps) => {
        return {
          title: format(`Title_${status}`, { prefix }),
          description: format(`RatioDesc_${status}`, {
            actualCount,
            progress: progress(actualCount, totalCount)
          })
        };
      },
      getDescriptionByFeature: ({ featureKey, enabled, count }: MessagesByFeatureProps) => {
        if (featureKey === 'EXPORT_TIMESHEET_INCLUDE_EMPTY_ALLOCATION' && count && count !== '0') {
          return `(${format(`DescFeature_${featureKey}_${enabled ? 'true' : 'false'}`, {
            count
          })})`;
        }
        return '';
      },
      getSourceName: ({ sourceType, name }: SourceNameProps) => {
        switch (sourceType) {
          case OcReportSourceTypeModel.MyReport:
            return `${format('myReport')} ${name}`;
          case OcReportSourceTypeModel.Hub:
            return `${format('quickView')} ${name}`;
          case OcReportSourceTypeModel.StandardReport:
            return `${format('standardReport')} ${name}`;
        }
        return undefined;
      },
      getEntityName: ({ entityType }: EntityNameProps) => {
        return entityType ? format(`${entityType}_single`) : '';
      },
      getTypeName: ({ type }: TypeNameProps) => {
        return {
          title: '',
          description: format(type === 'CSV_EXPORT' ? 'csv' : 'excel')
        };
      }
    };
  }, [format]);
}

function progress(actualCount: number | null | undefined, totalCount: number | null | undefined) {
  if (!isNumber(totalCount) || !isNumber(actualCount)) {
    return 0;
  }
  const result = Math.round((actualCount / totalCount) * 100);
  return isNumber(result) ? result : 0;
}
